import { DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Input, message, Modal } from 'antd';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import Page1 from './Page1';
import Page2 from './Page2';
import Page3 from './Page3';
import PDFHeader from './PDFHeader';

function SmartUserAccessRequestForm() {
	const page1Ref = useRef(null);
	const page2Ref = useRef(null);
	const page3Ref = useRef(null);
	const headerRef = useRef(null);
	const [, setLoading] = useState(false);
	// const versionRef = useRef(null);
	// const authorRef = useRef(null);
	// const dateRef = useRef(null);
	// const changeRef = useRef(null);
	const managerSignatureRef = useRef(null);
	const approverSignatureRef = useRef(null);

	const [signatureModalVisible, setSignatureModalVisible] = useState(false);
	const [currentSignatureType, setCurrentSignatureType] = useState('manager');
	const [signatures, setSignatures] = useState({
		manager: {
			image: null,
			name: '',
			timestamp: null,
		},
		approver: {
			image: null,
			name: '',
			timestamp: null,
		},
	});

	const [tempSignerName, setTempSignerName] = useState('');

	const handleOpenSignatureModal = type => {
		setCurrentSignatureType(type);
		setTempSignerName(signatures[type].name || '');
		setSignatureModalVisible(true);
	};
	const formatTimestamp = date => {
		if (!date) return '';

		const options = {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: true,
		};

		return date.toLocaleString('en-US', options);
	};

	const handleSaveSignature = async () => {
		const signatureRef =
			currentSignatureType === 'manager'
				? managerSignatureRef
				: approverSignatureRef;

		if (signatureRef.current.isEmpty()) {
			message.warning('Please provide a signature');
			return;
		}

		if (!tempSignerName.trim()) {
			message.warning('Please enter your name');
			return;
		}

		const signatureImage = signatureRef.current.toDataURL();
		const timestamp = new Date();

		setSignatures({
			...signatures,
			[currentSignatureType]: {
				image: signatureImage,
				name: tempSignerName,
				timestamp: formatTimestamp(timestamp),
			},
		});

		setSignatureModalVisible(false);
		message.success(
			`${
				currentSignatureType === 'manager' ? 'Manager' : 'Reviewer'
			} signature saved with timestamp`,
		);
	};

	const handleClearSignature = () => {
		const signatureRef =
			currentSignatureType === 'manager'
				? managerSignatureRef
				: approverSignatureRef;
		signatureRef.current.clear();
	};

	const [versions] = useState([
		{
			key: '1',
			version: 'V1.0',
			author: 'Anuja Nair',
			date: '01-Nov-2023 ',
			change: 'Initial Form ',
		},
		{
			key: '2',
			version: 'V2.0',
			author: 'Chiara Morganti',
			date: '01-Jan-2024',
			change: 'Form Change',
		},
		{
			key: '3',
			version: 'V3.0',
			author: 'Chiara Morganti',
			date: '15-Oct-2025',
			change: 'Form Change, User = requestor',
		},
	]);

	// const handleAddVersion = () => {
	// 	const newVersion = {
	// 		key: versionRef.current.input.value,
	// 		version: versionRef.current.input.value,
	// 		change: changeRef.current.input.value,
	// 		author: authorRef.current.input.value,
	// 		date: dateRef.current.input.value,
	// 	};
	// 	setVersions([...versions, newVersion]);
	// };

	const handleDownloadPDF = async () => {
		const page1El = page1Ref.current;
		const page2El = page2Ref.current;
		const page3El = page3Ref.current;
		const headerElement = headerRef.current;
		const pageElements = [page1El, page2El, page3El];

		if (!page1El || !page2El || !page3El || !headerElement) {
			message.error('Form not found');
			return;
		}

		setLoading(true);
		const hideMessage = message.loading('Generating PDF...', 0);

		try {
			// Step 1: Capture header
			const headerCanvas = await html2canvas(headerElement, {
				scale: 2,
				useCORS: true,
				logging: false,
				backgroundColor: '#ffffff',
			});

			// Step 3: Create PDF
			const pdf = jsPDF({
				orientation: 'portrait',
				unit: 'mm',
				format: 'a4',
				compress: true,
			});

			const pdfWidth = pdf.internal.pageSize.getWidth();
			const pdfHeight = pdf.internal.pageSize.getHeight();

			// Calculate header dimensions
			const headerImgData = headerCanvas.toDataURL('image/png');
			const headerAspectRatio = headerCanvas.width / headerCanvas.height;
			const headerHeightInMM = pdfWidth / headerAspectRatio;

			const topMargin = headerHeightInMM + 4;

			// Generate pages
			for (let page = 0; page < pageElements.length; page += 1) {
				if (page > 0) {
					pdf.addPage();
				}

				// Add header to every page
				pdf.addImage(
					headerImgData,
					'PNG',
					0,
					0,
					pdfWidth,
					headerHeightInMM,
					'',
					'FAST',
				);

				// eslint-disable-next-line no-await-in-loop
				const pageCanvas = await html2canvas(pageElements[page], {
					scale: 2,
					useCORS: true,
					logging: false,
					backgroundColor: '#ffffff',
				});

				const pageImgData = pageCanvas.toDataURL('image/png');
				const pageRatio = pdfWidth / pageCanvas.width;
				const pageContentHeight = pageCanvas.height * pageRatio;

				// Add page content
				pdf.addImage(
					pageImgData,
					'PNG',
					0,
					topMargin,
					pdfWidth,
					pageContentHeight,
					'',
					'FAST',
				);

				// Add page number
				pdf.setFontSize(10);
				pdf.setTextColor(80, 80, 80);
				pdf.setFont('helvetica', 'normal');
				pdf.text(`Page ${page + 1} of 3`, pdfWidth / 2, pdfHeight - 8, {
					align: 'center',
				});
			}

			const filename = `SMART_User_Access_Request_${
				new Date().toISOString().split('T')[0]
			}.pdf`;
			pdf.save(filename);

			hideMessage();
			message.success('PDF downloaded successfully!');
		} catch (error) {
			hideMessage();
			message.error('Failed to generate PDF');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gray-100 register">
			<PDFHeader ref={headerRef} />
			<div className="bg-white" ref={page1Ref}>
				<div className="max-w-6xl mx-auto p-6 bg-white">
					<Page1 />
				</div>
			</div>
			<div className="bg-white" ref={page2Ref}>
				<div className="max-w-6xl mx-auto p-6 bg-white">
					<Page2 manager={signatures?.manager} />{' '}
				</div>{' '}
			</div>
			<div className="bg-white" ref={page3Ref}>
				<div className="max-w-6xl mx-auto p-6 bg-white">
					<Page3 data={versions} reviewer={signatures?.approver} />{' '}
				</div>{' '}
			</div>
			{/* User Details Table */}
			{/* <div className="p-6 bg-white">
				<div className="border-2 border-black">
					{[
						{
							label: 'Version',
							key: 'version',
							refObj: versionRef,
						},
						{
							label: 'Author',
							key: 'author',
							refObj: authorRef,
						},
						{
							label: 'Date',
							key: 'date',
							refObj: dateRef,
						},
						{
							label: 'Change',
							key: 'change',
							refObj: changeRef,
						},
					].map((field, index, array) => (
						<div
							key={field.key}
							className={`grid grid-cols-2 ${
								index < array.length - 1 ? 'border-b-2 border-black' : ''
							}`}>
							<div className="border-r-2 border-black p-3 bg-white">
								<span className="font-bold text-sm">{field.label}</span>
							</div>
							<div className="p-0">
								<Input
									className="border-gray-300 rounded-5 h-full"
									ref={field.refObj}
								/>
							</div>
						</div>
					))}
				</div>
			</div> */}
			{/* <div className="bg-white text-center">
				<Button
					type="primary"
					danger
					onClick={handleAddVersion}
					size="large"
					className="bg-red-600 hover:bg-red-700">
					Add latest version
				</Button>
			</div> */}
			<div className="p-6 bg-white">
				<Button
					type="default"
					icon={<EditOutlined />}
					className="bg-[#CA001B]"
					onClick={() => handleOpenSignatureModal('manager')}>
					{signatures.manager.image
						? 'Update Manager Signature'
						: 'Add Manager Signature'}
				</Button>

				<Button
					type="default"
					icon={<EditOutlined />}
					className="bg-[#CA001B]"
					onClick={() => handleOpenSignatureModal('approver')}>
					{signatures.approver.image
						? 'Update Reviewer Signature'
						: 'Add Reviewer Signature'}
				</Button>
			</div>

			<div className="bg-white pb-20 text-center">
				<Button
					type="primary"
					color="#CA001B"
					icon={<DownloadOutlined />}
					onClick={handleDownloadPDF}
					size="large">
					Download as PDF
				</Button>
			</div>

			<Modal
				title={`Sign Document - ${
					currentSignatureType === 'manager' ? 'Manager' : 'Reviewer'
				}`}
				className="registermodal"
				open={signatureModalVisible}
				onOk={handleSaveSignature}
				onCancel={() => setSignatureModalVisible(false)}
				width={600}
				okText="Save Signature with Timestamp"
				cancelText="Cancel">
				<div className="mb-4">
					<label className="block text-sm font-medium mb-2">Your Name:</label>
					<Input
						placeholder="Enter your full name"
						value={tempSignerName}
						onChange={e => setTempSignerName(e.target.value)}
						size="large"
					/>
				</div>

				<div className="mb-4">
					<label className="block text-sm font-medium mb-2">Signature:</label>
					<div className="border-2 border-gray-300 rounded">
						{currentSignatureType === 'manager' ? (
							<SignatureCanvas
								key="manager-canvas"
								ref={managerSignatureRef}
								canvasProps={{
									width: 550,
									height: 200,
									className: 'signature-canvas',
								}}
								backgroundColor="white"
							/>
						) : (
							<SignatureCanvas
								key="approver-canvas"
								ref={approverSignatureRef}
								canvasProps={{
									width: 550,
									height: 200,
									className: 'signature-canvas',
								}}
								backgroundColor="white"
							/>
						)}
					</div>
				</div>

				<Button onClick={handleClearSignature} block>
					Clear Signature
				</Button>
			</Modal>
		</div>
	);
}

export default SmartUserAccessRequestForm;
